import { jsx } from "react/jsx-runtime";
import { useRef, useState, useEffect } from "react";
const ScrubberSlider = ({
  defaultValue,
  onChange,
  id,
  chartHeight,
  chartAreaTop,
  chartAreaBottom,
  chartAreaLeft,
  chartAreaRight
}) => {
  const canvasRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const sliderInitialized = useRef(false);
  const prevChartAreaLeft = useRef(0);
  const prevChartAreaRight = useRef(0);
  const isDraggingRef = useRef(false);
  const [linePosition, setLinePosition] = useState(defaultValue);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [containerWidth, setContainerWidth] = useState({
    width: 0
  });
  const maxValue = 30;
  const lineColor = "rgba(0, 0, 0, 0.5)";
  const handleFillColor = "white";
  const handleBorderColor = "#d3d3d3";
  const sliderRadius = 9;
  const handleRadius = 9;
  const padding = 15;
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const canvasWidth = canvas.clientWidth;
    const canvasHeight = canvas.clientHeight;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const areaLeft = chartAreaLeft && chartAreaLeft > 0 ? chartAreaLeft : padding;
    const areaRight = chartAreaRight && chartAreaRight > 0 ? chartAreaRight : canvasWidth - padding;
    const areaWidth = areaRight - areaLeft;
    const yearRange = maxValue - 1;
    const yearToPixel = (year) => areaLeft + (year - 1) / yearRange * areaWidth;
    const areaLeftChanged = areaLeft !== prevChartAreaLeft.current && areaLeft !== padding;
    const areaRightChanged = areaRight !== prevChartAreaRight.current;
    if ((!sliderInitialized.current || areaLeftChanged || areaRightChanged) && canvasWidth > 0) {
      sliderInitialized.current = true;
      prevChartAreaLeft.current = areaLeft;
      prevChartAreaRight.current = areaRight;
      setSliderPosition(Math.max(areaLeft, Math.min(yearToPixel(linePosition), areaRight)));
      return;
    }
    const effectiveBottom = chartAreaBottom && chartAreaBottom > 0 ? chartAreaBottom : canvasHeight;
    const handleCenterY = effectiveBottom - sliderRadius;
    const isOverHandle = (canvasX, canvasY) => {
      const dx = canvasX - sliderPosition;
      const dy = canvasY - handleCenterY;
      return Math.sqrt(dx * dx + dy * dy) <= handleRadius + 4;
    };
    const drawSliderScrubber = () => {
      const effectiveTop = chartAreaTop && chartAreaTop > 0 ? chartAreaTop : 0;
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.moveTo(sliderPosition, effectiveTop);
      ctx.lineTo(sliderPosition, effectiveBottom);
      ctx.strokeStyle = lineColor;
      ctx.stroke();
      const numSteps = yearRange;
      const stepSize = areaWidth / numSteps;
      for (let i = 0; i <= numSteps; i += 1) {
        const xPos = areaLeft + stepSize * i;
        ctx.beginPath();
        ctx.moveTo(xPos, effectiveBottom - sliderRadius);
        ctx.lineTo(xPos, effectiveBottom - sliderRadius - 0.1);
        ctx.strokeStyle = lineColor;
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(sliderPosition, handleCenterY, sliderRadius, 0, 2 * Math.PI);
      ctx.beginPath();
      ctx.arc(sliderPosition, handleCenterY, handleRadius, 0, 2 * Math.PI);
      ctx.fillStyle = handleFillColor;
      ctx.fill();
      ctx.strokeStyle = handleBorderColor;
      ctx.lineWidth = 2;
      ctx.stroke();
    };
    const handleSliderMove = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const canvasRect = canvas.getBoundingClientRect();
      const canvasX = event.clientX - canvasRect.left;
      const canvasY = event.clientY - canvasRect.top;
      if (isDraggingRef.current) {
        const clampedX = Math.min(Math.max(areaLeft, canvasX), areaRight);
        const sliderValue = Math.round((clampedX - areaLeft) / areaWidth * yearRange) + 1;
        const stepIndex = Math.min(maxValue, Math.max(1, sliderValue));
        if (isNaN(stepIndex)) return;
        setSliderPosition(clampedX);
        setLinePosition(stepIndex);
        onChangeRef.current(stepIndex);
      } else {
        canvas.style.cursor = isOverHandle(canvasX, canvasY) ? "grab" : "default";
      }
    };
    const handleMouseDown = (event) => {
      const canvasRect = canvas.getBoundingClientRect();
      const canvasX = event.clientX - canvasRect.left;
      const canvasY = event.clientY - canvasRect.top;
      if (isOverHandle(canvasX, canvasY)) {
        isDraggingRef.current = true;
        canvas.style.cursor = "grabbing";
      }
    };
    const handleMouseUp = (event) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      const canvasRect = canvas.getBoundingClientRect();
      const canvasX = event.clientX - canvasRect.left;
      const canvasY = event.clientY - canvasRect.top;
      canvas.style.cursor = isOverHandle(canvasX, canvasY) ? "grab" : "default";
    };
    const handleTouchMove = (event) => {
      var _a, _b;
      event.preventDefault();
      const touchX = ((_b = (_a = event.touches) == null ? void 0 : _a[0]) == null ? void 0 : _b.clientX) - canvas.getBoundingClientRect().left;
      const clampedX = Math.min(Math.max(areaLeft, touchX), areaRight);
      const sliderValue = Math.round((clampedX - areaLeft) / areaWidth * yearRange) + 1;
      const stepIndex = Math.min(maxValue, Math.max(1, sliderValue));
      if (isNaN(stepIndex)) return;
      setSliderPosition(clampedX);
      setLinePosition(stepIndex);
      onChangeRef.current(stepIndex);
    };
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mousemove", handleSliderMove);
    window.addEventListener("mouseup", handleMouseUp);
    canvas.addEventListener("touchmove", handleTouchMove);
    canvas.addEventListener("touchend", handleTouchMove);
    drawSliderScrubber();
    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mousemove", handleSliderMove);
      window.removeEventListener("mouseup", handleMouseUp);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleTouchMove);
    };
  }, [containerWidth, linePosition, sliderPosition, defaultValue, chartAreaTop, chartAreaBottom, chartAreaLeft, chartAreaRight]);
  useEffect(() => {
    var _a;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const {
          width
        } = entry.contentRect;
        setContainerWidth({
          width
        });
      });
    });
    const parentContainer = (_a = canvasRef.current) == null ? void 0 : _a.parentNode;
    if (!parentContainer) return;
    resizeObserver.observe(parentContainer);
    return () => {
      resizeObserver.unobserve(parentContainer);
    };
  }, []);
  return /* @__PURE__ */ jsx("canvas", { ref: canvasRef, style: {
    width: "100%",
    height: chartHeight ? `${chartHeight}px` : "100%",
    display: "block"
  }, id });
};
export {
  ScrubberSlider as default
};
